:host {
  display: block;
  box-sizing: border-box;
  inline-size: 100%;
  background-color: var(--footer-container-background);
  color: var(--footer-container-color);
  font-family: var(--footer-container-font-family);
}

:host([variant='simple']) {
  /* Simple variant only renders the legal bar — no extra container padding. */
  background-color: transparent;
}

/* ---------- Primary block (EVO variant) ---------- */

.primary {
  display: flex;
  flex-direction: column;
  inline-size: 100%;
  max-inline-size: var(--footer-container-max-width);
  margin-inline: auto;
  padding-inline: var(--footer-container-padding-inline-desktop);
  padding-block-start: var(--footer-container-padding-block-start-desktop);
  padding-block-end: var(--footer-primary-padding-block-end);
  gap: var(--footer-primary-gap-desktop);
  box-sizing: border-box;
}

.primary-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--footer-primary-gap-desktop);
  flex-wrap: wrap;
}

/* ---------- Branding ---------- */

.branding {
  display: flex;
  flex-direction: column;
  gap: var(--footer-branding-gap);
  flex: 1 1 0;
  min-inline-size: 0;
  max-inline-size: var(--footer-branding-max-width);
}

.branding-default {
  display: flex;
  flex-direction: column;
  gap: var(--footer-branding-gap);
}

.branding-headline {
  margin: 0;
  font-family: var(--footer-container-font-family);
  font-size: var(--footer-branding-headline-font-size);
  line-height: var(--footer-branding-headline-line-height);
  font-weight: var(--footer-branding-headline-font-weight);
  letter-spacing: var(--footer-branding-headline-letter-spacing);
  color: var(--footer-branding-headline-color);
}

/* ---------- Divider ---------- */

.divider {
  inline-size: 100%;
  block-size: var(--footer-divider-width);
  background-color: var(--footer-divider-color);
}

/* ---------- Primary grid (columns + aside) ---------- */

.primary-grid {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1.2fr);
  gap: var(--footer-columns-gap-desktop);
  align-items: flex-start;
}

/* ---------- Link columns ---------- */

.columns {
  display: grid;
  grid-template-columns: repeat(4, minmax(var(--footer-columns-min-width), 1fr));
  gap: var(--footer-columns-gap-desktop);
}

.column {
  display: flex;
  flex-direction: column;
  gap: var(--footer-column-gap);
  min-inline-size: 0;
}

.column-title {
  margin: 0;
  font-family: var(--footer-container-font-family);
  font-size: var(--footer-column-title-font-size);
  line-height: var(--footer-column-title-line-height);
  font-weight: var(--footer-column-title-font-weight);
  color: var(--footer-column-title-color);
}

.column-list {
  display: flex;
  flex-direction: column;
  gap: var(--footer-column-link-gap);
  padding: 0;
  margin: 0;
  list-style: none;
}

.column-item {
  display: flex;
  align-items: center;
}

.column-item mud-link {
  display: inline-flex;
  inline-size: 100%;
}

/* ---------- Aside (contact + social) ---------- */

.primary-aside {
  display: flex;
  flex-direction: column;
  gap: var(--footer-contact-gap);
  min-inline-size: 0;
}

/* ---------- Contact ---------- */

.contact {
  display: flex;
  flex-direction: column;
  gap: var(--footer-contact-gap);
}

.contact-heading,
.social-heading,
.partners-heading {
  margin: 0;
  font-family: var(--footer-container-font-family);
  font-size: var(--footer-column-title-font-size);
  line-height: var(--footer-column-title-line-height);
  font-weight: var(--footer-column-title-font-weight);
  color: var(--footer-column-title-color);
}

.contact-list,
.social-list,
.partners-list {
  display: flex;
  flex-direction: column;
  gap: var(--footer-contact-row-gap);
  padding: 0;
  margin: 0;
  list-style: none;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-12, 12px);
  font-size: var(--footer-contact-label-font-size);
  line-height: var(--footer-contact-label-line-height);
  color: var(--footer-contact-label-color);
}

.contact-item mud-icon {
  --icon-color: var(--footer-contact-icon-color);
  flex-shrink: 0;
  inline-size: var(--footer-contact-icon-size);
  block-size: var(--footer-contact-icon-size);
  margin-block-start: 2px;
}

.contact-link {
  color: inherit;
  text-decoration: none;
  border-radius: var(--border-radius-4, 4px);
}

.contact-link:hover {
  text-decoration: underline;
}

.contact-link:focus-visible {
  outline: var(--footer-focus-ring-width) solid var(--footer-focus-ring-color);
  outline-offset: var(--footer-focus-ring-offset);
}

.contact-item--address .contact-address {
  max-inline-size: var(--footer-contact-address-max-width);
}

/* ---------- Social ---------- */

.social {
  display: flex;
  flex-direction: column;
  gap: var(--footer-social-group-gap);
}

.social-list {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--footer-social-gap);
}

.social-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--footer-social-button-size);
  block-size: var(--footer-social-button-size);
  border-radius: var(--footer-social-button-border-radius);
  background-color: var(--footer-social-button-background);
  color: var(--footer-social-button-color);
  text-decoration: none;
  transition:
    background-color 150ms ease-out,
    color 150ms ease-out;
}

.social-link mud-icon {
  --icon-color: currentcolor;
  inline-size: var(--footer-social-icon-size);
  block-size: var(--footer-social-icon-size);
}

.social-link:hover {
  background-color: var(--footer-social-button-background-hover);
}

.social-link:focus-visible {
  outline: var(--footer-focus-ring-width) solid var(--footer-focus-ring-color);
  outline-offset: var(--footer-focus-ring-offset);
}

/* ---------- Partners ---------- */

.partners {
  display: flex;
  flex-direction: column;
  gap: var(--footer-partners-row-gap);
  padding-block-start: var(--footer-partners-row-gap);
  border-block-start: var(--footer-divider-width) solid var(--footer-divider-color);
}

.partners-list {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--footer-partners-gap);
}

.partner-item {
  display: inline-flex;
  align-items: center;
}

.partner-link {
  display: inline-flex;
  text-decoration: none;
  color: inherit;
  border-radius: var(--border-radius-4, 4px);
}

.partner-link:focus-visible {
  outline: var(--footer-focus-ring-width) solid var(--footer-focus-ring-color);
  outline-offset: var(--footer-focus-ring-offset);
}

.partner-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-8, 8px);
}

.partner-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 40px;
  block-size: 40px;
  border-radius: var(--border-radius-4, 4px);
  background-color: var(--color-background-base-secondary, #f5f5f5);
  color: var(--color-text-base-default, #121212);
  font-size: var(--font-size-12, 12px);
  font-weight: var(--font-weight-semibold, 600);
  letter-spacing: 0.5px;
}

.partner-label {
  font-size: var(--footer-partners-label-font-size);
  line-height: var(--footer-partners-label-line-height);
  color: var(--footer-partners-label-color);
  max-inline-size: 140px;
}

/* ---------- Accessibility statement ---------- */

.accessibility {
  margin: 0;
  margin-block-start: var(--footer-accessibility-margin-block-start);
  font-size: var(--footer-accessibility-font-size);
  line-height: var(--footer-accessibility-line-height);
  color: var(--footer-accessibility-color);
}

/* ---------- Locale switcher ---------- */

.locale {
  position: relative;
  flex-shrink: 0;
}

.locale-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--footer-locale-gap);
  padding-block: var(--footer-locale-padding-block);
  padding-inline: var(--footer-locale-padding-inline);
  border: 0;
  border-radius: var(--footer-locale-border-radius);
  background-color: var(--footer-locale-background);
  color: var(--footer-locale-color);
  font-family: var(--footer-container-font-family);
  font-size: var(--footer-locale-font-size);
  line-height: var(--footer-locale-line-height);
  font-weight: var(--footer-locale-font-weight);
  cursor: pointer;
  transition: background-color 150ms ease-out;
}

.locale-trigger mud-icon {
  --icon-color: currentcolor;
  inline-size: var(--footer-locale-icon-size);
  block-size: var(--footer-locale-icon-size);
  flex-shrink: 0;
}

.locale-trigger:hover {
  background-color: var(--footer-locale-background-hover);
}

.locale-trigger:focus-visible {
  outline: var(--footer-focus-ring-width) solid var(--footer-focus-ring-color);
  outline-offset: var(--footer-focus-ring-offset);
}

.locale-menu {
  position: absolute;
  inset-inline-end: 0;
  inset-block-end: calc(100% + var(--spacing-4, 4px));
  min-inline-size: var(--footer-locale-menu-min-width);
  margin: 0;
  padding: var(--spacing-4, 4px);
  list-style: none;
  background-color: var(--footer-locale-menu-background);
  border: var(--border-width-1, 1px) solid var(--footer-locale-menu-border);
  border-radius: var(--footer-locale-menu-border-radius);
  box-shadow: var(--footer-locale-menu-shadow);
  z-index: 10;
}

.locale-option {
  padding-block: var(--footer-locale-menu-item-padding-block);
  padding-inline: var(--footer-locale-menu-item-padding-inline);
  border-radius: var(--border-radius-4, 4px);
  font-size: var(--footer-locale-font-size);
  line-height: var(--footer-locale-line-height);
  cursor: pointer;
  color: var(--footer-locale-color);
}

.locale-option:hover {
  background-color: var(--footer-locale-menu-item-background-hover);
}

.locale-option:focus-visible {
  outline: var(--footer-focus-ring-width) solid var(--footer-focus-ring-color);
  outline-offset: -2px;
}

.locale-option--active {
  background-color: var(--footer-locale-menu-item-active-background);
  color: var(--footer-locale-menu-item-active-color);
  font-weight: var(--footer-locale-font-weight);
}

/* ---------- Legal bar ---------- */

.legal-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--footer-legal-gap);
  padding-inline: var(--footer-legal-padding-inline-desktop);
  padding-block: var(--footer-legal-padding-block-desktop);
  background-color: var(--footer-legal-background);
  color: var(--footer-legal-color);
  font-size: var(--footer-legal-font-size);
  line-height: var(--footer-legal-line-height);
  font-weight: var(--footer-legal-font-weight);
}

.legal-copyright {
  margin: 0;
}

.legal-links {
  display: flex;
  gap: var(--footer-legal-gap);
}

.legal-link {
  color: inherit;
  text-decoration: none;
  border-radius: var(--border-radius-4, 4px);
}

.legal-link:hover {
  text-decoration: underline;
}

.legal-link:focus-visible {
  outline: var(--footer-focus-ring-width) solid var(--footer-focus-ring-color);
  outline-offset: var(--footer-focus-ring-offset);
}

/* ---------- Responsive: tablet ---------- */

@media (max-width: 1280px) {
  .primary {
    padding-inline: var(--footer-container-padding-inline-tablet);
    gap: var(--footer-primary-gap-mobile);
  }

  .primary-grid {
    grid-template-columns: 1fr;
    gap: var(--footer-primary-gap-mobile);
  }

  .primary-aside {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--footer-columns-gap-tablet);
    align-items: flex-start;
  }

  .legal-bar {
    padding-inline: var(--footer-container-padding-inline-tablet);
  }
}

@media (max-width: 900px) {
  .columns {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--footer-columns-gap-tablet);
  }
}

/* ---------- Responsive: mobile ---------- */

@media (max-width: 640px) {
  .primary {
    padding-inline: var(--footer-container-padding-inline-mobile);
    padding-block-start: var(--footer-container-padding-block-start-mobile);
    padding-block-end: var(--footer-container-padding-block-end-mobile);
    gap: var(--footer-primary-gap-mobile);
  }

  .primary-header {
    flex-direction: column;
    align-items: stretch;
  }

  .branding-headline {
    font-size: var(--font-size-24, 24px);
    line-height: var(--line-height-32, 32px);
  }

  .columns {
    grid-template-columns: 1fr;
    gap: var(--footer-columns-gap-mobile);
  }

  .partners-list {
    flex-direction: column;
    align-items: flex-start;
  }

  .legal-bar {
    padding-inline: var(--footer-legal-padding-inline-mobile);
    padding-block: var(--footer-legal-padding-block-mobile);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-8, 8px);
  }

  .locale-menu {
    inset-inline-end: auto;
    inset-inline-start: 0;
  }
}

/* ---------- Reduced motion ---------- */

@media (prefers-reduced-motion: reduce) {
  .social-link,
  .locale-trigger,
  .contact-link {
    transition: none;
  }
}
